import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import {
  apiClient,
  type ApiResponse,
  type Brand as ApiBrand,
  type BrandRating as ApiBrandRating,
} from '../../services/api';

export type Brand = ApiBrand;
export type BrandRating = ApiBrandRating;

interface BrandsState {
  brands: Brand[];
  currentBrand: Brand | null;
  currentRating: BrandRating | null;
  searchResults: Brand[];
  isLoading: boolean;
  error: string | null;
  searchQuery: string;
  filters: {
    categories: string[];
    minRating: number;
    certifications: string[];
  };
}

const initialState: BrandsState = {
  brands: [],
  currentBrand: null,
  currentRating: null,
  searchResults: [],
  isLoading: false,
  error: null,
  searchQuery: '',
  filters: {
    categories: [],
    minRating: 0,
    certifications: [],
  },
};

// Async thunks
export const searchBrands = createAsyncThunk(
  'brands/search',
  async (
    params: { query: string; filters?: Partial<BrandsState['filters']> },
    { rejectWithValue }
  ) => {
    try {
      // 使用统一 apiClient，当前仅按关键词搜索；其他 filters 可按后端支持逐步映射
      const response = await apiClient.get<ApiResponse<unknown[]>>(
        `/brands${params.query ? `?search=${encodeURIComponent(params.query)}` : ''}`
      );
      return response.data;
    } catch (error) {
      return rejectWithValue((error as Error).message || 'Search failed');
    }
  }
);

export const fetchBrandById = createAsyncThunk(
  'brands/fetchById',
  async (brandId: string, { rejectWithValue }) => {
    try {
      const response = await apiClient.get<ApiResponse<unknown>>(
        `/brands/${brandId}`
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        (error as Error).message || 'Failed to fetch brand'
      );
    }
  }
);

export const fetchBrandRating = createAsyncThunk(
  'brands/fetchRating',
  async (brandId: string, { rejectWithValue }) => {
    try {
      const response = await apiClient.get<ApiResponse<unknown>>(
        `/ratings/brand/${brandId}`
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        (error as Error).message || 'Failed to fetch rating'
      );
    }
  }
);

export const fetchTrendingBrands = createAsyncThunk(
  'brands/fetchTrending',
  async (category: string | undefined, { rejectWithValue }) => {
    try {
      // 后端若有分类过滤支持，可在 apiClient 新增参数。这里先调用统一的 trending 列表。
      const response = await apiClient.getTrendingBrands();
      return response.data;
    } catch (error) {
      return rejectWithValue(
        (error as Error)?.message || 'Failed to fetch trending brands'
      );
    }
  }
);

const brandsSlice = createSlice({
  name: 'brands',
  initialState,
  reducers: {
    clearError: state => {
      state.error = null;
    },
    setSearchQuery: (state, action: PayloadAction<string>) => {
      state.searchQuery = action.payload;
    },
    setFilters: (
      state,
      action: PayloadAction<Partial<BrandsState['filters']>>
    ) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    clearCurrentBrand: state => {
      state.currentBrand = null;
      state.currentRating = null;
    },
    clearSearchResults: state => {
      state.searchResults = [];
      state.searchQuery = '';
    },
  },
  extraReducers: builder => {
    builder
      // Search brands
      .addCase(searchBrands.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(searchBrands.fulfilled, (state, action) => {
        state.isLoading = false;
        state.searchResults = action.payload as unknown as Brand[];
      })
      .addCase(searchBrands.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      // Fetch brand by ID
      .addCase(fetchBrandById.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchBrandById.fulfilled, (state, action) => {
        state.isLoading = false;
        state.currentBrand = action.payload as unknown as Brand;
      })
      .addCase(fetchBrandById.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      // Fetch brand rating
      .addCase(fetchBrandRating.fulfilled, (state, action) => {
        state.currentRating = action.payload as unknown as BrandRating;
      })
      // Fetch trending brands
      .addCase(fetchTrendingBrands.fulfilled, (state, action) => {
        state.brands = action.payload as unknown as Brand[];
      });
  },
});

export const {
  clearError,
  setSearchQuery,
  setFilters,
  clearCurrentBrand,
  clearSearchResults,
} = brandsSlice.actions;

export default brandsSlice.reducer;
